/*
 * Brora gift-wrap engine — core (config read + app-proxy cart write + no-reload hook).
 *
 * Reimplements the À La Carte "Wrapped" gift-wrap UI in the theme's own light DOM (our buttons + drawer live in
 * Liquid/JS — see snippets/gift-wrap-*.liquid + assets/gift-wrap-ui.js). The app stays installed and owns the
 * "giftbox" fee product, the per-market config and the cart write.
 *
 * Cart WRITE — via the app, not ourselves. In its current (non-bundle) mode the app actively OWNS the cart: it
 * intercepts /cart AJAX, strips the Gifting properties off any write it didn't originate, and deletes "orphan"
 * giftbox fee lines — so writing the cart directly no longer survives. Instead we read our config + collect the
 * gift fields in our own drawer, then feed those values into the app's matching (hidden) <wrapped-gift-block>
 * and call its public saveGifting() / removeGifting() (see the app-proxy section). The app then writes the cart
 * exactly as its own UI would (gifted product line + a "Gift Wrap" fee line, same token), which also keeps this
 * working across BOTH app modes (bundle + non-bundle) since the app handles the shape.
 *   - product (PDP): the global block's saveGifting() STAMPS the product form; the theme's add-to-cart carries it.
 *   - drawer (per line / order): the block co-located in the mini-bag anchor; saveGifting() writes the cart.
 *
 * Config comes from window.Wrapped.options[mode] (fields, labels, fee, banner image).
 *
 * WARNING — coupling: depends on window.Wrapped.options (config) + the app's <wrapped-gift-block> element and
 * its saveGifting/removeGifting methods + shadow-form input names (receiver/sender/message). A Wrapped app
 * update can change these; guarded reads + a rejected promise / console warning flag drift.
 */
(function () {
  'use strict';

  // ── config ───────────────────────────────────────────────────────────────
  // Normalised view of window.Wrapped.options[mode] (mode: 'product' | 'order'). Defensive: every consumer
  // must tolerate a missing app config (returns null) rather than throw.
  const rawOptions = () => (window.Wrapped && window.Wrapped.options) || null;
  const localised = (labelObj, fallback) => {
    if (!labelObj) return fallback;
    const market = window.Wrapped && window.Wrapped.settings && window.Wrapped.settings.market;
    return labelObj[market] || labelObj.primary || fallback;
  };
  function config(mode) {
    const opts = rawOptions();
    const cfg = opts && opts[mode];
    if (!cfg) return null;
    const s = cfg.settings || {};
    const product = cfg.product || {};
    const variants = (product.variants && (product.variants.edges || product.variants)) || [];
    const feeVariant = variants.length ? (variants[0].node || variants[0]) : null;
    return {
      mode: mode,
      // enabled + required fields, in display order
      fields: [
        { key: 'receiver', enabled: s.giftReceiver !== false, required: !!s.giftReceiverRequired, label: localised(s.giftReceiverLabels, 'Recipient name') },
        { key: 'sender', enabled: s.giftSender !== false, required: !!s.giftSenderRequired, label: localised(s.giftSenderLabels, 'Sender name') },
        { key: 'message', enabled: s.giftMessage !== false, required: !!s.giftMessageRequired, label: localised(s.giftMessageLabels, 'Gift Message'), maxlength: parseInt(s.giftMessageMaxChars, 10) || 0 }
      ],
      labels: { save: localised(s.saveLabels, 'Save'), remove: localised(s.removeLabels, 'Remove') },
      fee: feeVariant ? { id: String(feeVariant.id), title: feeVariant.title, price: feeVariant.price } : null,
      image: product.image || null
    };
  }

  // ── app proxy (the app owns the cart write) ──────────────────────────────
  // We feed our drawer values into the app's matching (hidden) <wrapped-gift-block> and call its public
  // saveGifting() / removeGifting(); the app then writes the cart (product-line props + the "Gift Wrap" fee
  // line) exactly as its own UI would. Works for BOTH app modes (the app handles bundling).
  //   product → the single global block (target-group="product-page"); saveGifting() STAMPS the product form
  //             (properties[...] inputs) + sets the app's state, then the theme's add-to-cart carries it.
  //   drawer  → the per-line / order block co-located in the mini-bag anchor; saveGifting() writes the cart.
  const APP_FIELD_SELECTORS = { receiver: 'input[name="receiver"]', sender: 'input[name="sender"]', message: 'textarea[name="message"]' };

  // Find the app's gift block for a mode. product = the global PDP block; drawer/order = the block the app
  // injected inside the given mini-bag anchor (co-located with our launcher).
  function appBlock(mode, scope) {
    if (mode === 'product') return document.querySelector('wrapped-gift-block[target-group="product-page"]');
    return scope ? scope.querySelector('wrapped-gift-block') : null;
  }
  // Mirror our collected {receiver,sender,message} into the app block's shadow-DOM form (native setter + input
  // event so the app's framework registers the values), matching how a user would type them.
  function fillAppForm(block, fields) {
    const sr = block && block.shadowRoot;
    if (!sr) return;
    Object.keys(APP_FIELD_SELECTORS).forEach((k) => {
      const el = sr.querySelector(APP_FIELD_SELECTORS[k]);
      if (!el) return;
      const proto = el.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
      Object.getOwnPropertyDescriptor(proto, 'value').set.call(el, fields[k] == null ? '' : fields[k]);
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
  // Fill + commit a gift through the app block. Returns the app's own (async) saveGifting promise.
  function appSaveGifting(block, fields) {
    if (!block || typeof block.saveGifting !== 'function') {
      return Promise.reject(new Error('gift-wrap: Wrapped app block unavailable — cannot save gift'));
    }
    fillAppForm(block, fields);
    try {
      const r = block.saveGifting();
      return r && typeof r.then === 'function' ? r : Promise.resolve(r);
    } catch (e) { return Promise.reject(e); }
  }
  // Remove a gift through the app block.
  function appRemoveGifting(block) {
    if (!block || typeof block.removeGifting !== 'function') {
      return Promise.reject(new Error('gift-wrap: Wrapped app block unavailable — cannot remove gift'));
    }
    try {
      const r = block.removeGifting();
      return r && typeof r.then === 'function' ? r : Promise.resolve(r);
    } catch (e) { return Promise.reject(e); }
  }

  // ── no-reload hook ───────────────────────────────────────────────────────
  // Wrapped's refreshCart() reloads the page after any cart mutation unless settings.processorCallback is set;
  // it awaits that instead. We install a callback that refreshes the mini-bag in place (dispatch
  // wrapped:cart-updated, which mini-bag.js debounces into a /cart.js re-render) so nothing ever full-reloads.
  function installNoReloadCallback() {
    const settings = window.Wrapped && window.Wrapped.settings;
    if (!settings) return false;
    if (!settings.processorCallback) {
      settings.processorCallback = () => {
        document.dispatchEvent(new CustomEvent('wrapped:cart-updated'));
        return Promise.resolve();
      };
    }
    return true;
  }
  if (!installNoReloadCallback()) {
    let tries = 0;
    const timer = setInterval(() => {
      if (installNoReloadCallback() || ++tries > 100) clearInterval(timer);
    }, 100);
  }

  // ── public API ───────────────────────────────────────────────────────────
  window.BroraGiftWrap = {
    config: config,
    appBlock: appBlock,
    appSaveGifting: appSaveGifting,
    appRemoveGifting: appRemoveGifting
  };
})();
